<template>
  <items-btn label="Create" v-bind="$attrs" :items="validActions" />
  <import-project-dialog v-model="showDialog" :companies="integratedCompanies" />
</template>

<script setup lang="ts">
import ItemsBtn from '@/components/items-btn.vue'
import ImportProjectDialog from '@/components/accounting/import-project-dialog.vue'
import type { Company } from '@quotetome/materials-api'

const { $api } = useNuxtApp()
const authStore = useAuthStore()
const router = useRouter()

const auth = computed(() => ({
  canGetQuotes: authStore.canGetQuotes,
  canCreatePO: authStore.canCreatePO,
  canManageCompany: authStore.canManageCompany,
  canManageInvoices: authStore.canManageInvoices,
  canUseExpenses: authStore.canUseExpenses,
  canUseRequisitions: authStore.canUseRequisitions,
}))

const integratedCompanies = ref<Company[]>([])
const showDialog = ref(false)

const actions = computed(() => {
  return [
    {
      label: 'Quote Request',
      icon: 'mso:shoppingmode',
      dataTest: 'get-quotes-link',
      auth: auth.value.canGetQuotes,
      to: { name: 'orders-new' },
      value: 'get-quotes',
    },
    {
      label: 'PO',
      icon: 'mso:package_2',
      dataTest: 'create-po-link',
      auth: auth.value.canCreatePO,
      to: { name: 'purchase-orders-new' },
      value: 'create-po',
    },
    {
      label: 'Expense',
      icon: 'mso:receipt_long',
      dataTest: 'create-expense',
      auth: auth.value.canUseExpenses,
      to: { name: 'expenses-new' },
      value: 'create-expense',
    },
    {
      label: 'Requisition',
      icon: 'mso:shopping_cart',
      dataTest: 'create-requisition',
      auth: auth.value.canUseRequisitions,
      to: { name: 'requisitions-new' },
      value: 'create-requisition',
    },
    {
      label: 'Invoice',
      icon: 'mso:attach_money',
      dataTest: 'create-invoice',
      auth: auth.value.canManageInvoices,
      to: { name: 'invoices-new' },
      value: 'create-invoice',
    },
    {
      label: 'Project',
      icon: 'mso:folder',
      dataTest: 'create-project',
      auth: auth.value.canManageCompany,
      onClick: () => createProject(),
      value: 'create-project',
      class: 'separator',
    },
    {
      label: 'User',
      icon: 'mso:group',
      dataTest: 'create-user',
      auth: auth.value.canManageCompany,
      to: { name: 'users-new' },
      value: 'create-user',
    },
  ]
})

const validActions = computed(() => actions.value.filter((action) => action.auth))
const createProject = () => {
  if (integratedCompanies.value.length > 0) {
    showDialog.value = true
  }
  else {
    router.push({ name: 'projects-new' })
  }
}
onMounted(async () => {
  const companies = await $api.v1.companies.list()
  integratedCompanies.value = companies.filter(company => company.accounting_integration)
})
</script>
<style lang="scss">
.separator {
  border-top: 1px solid rgb(var(--v-theme-light-grey)) !important;
}
</style>
